import { AppEntrypoint, MSO } from "../typings/enums";

export interface ProductRouteResponse {
  product_name: string;
  app_entrypoint: AppEntrypoint;
  mso?: MSO;
}

export interface GetAppEntrypointParams {
  productName?: string;
  source?: string;
  isDisqualified?: boolean;
  userAgent?: string;
  surveySessionId?: string;
}

/**
 * Service for fetching app entrypoint data from the new product-route API endpoint
 */
export class AppEntrypointService {
  private static baseUrl =
    import.meta.env.VITE_MSO_GATEWAY_BASE_URL ||
    "https://mso-gateway-staging.fh.co";

  /**
   * Fetches app entrypoint data for a specific product from the API
   * @param productName - The product name to fetch route data for
   * @returns Promise<ProductRouteResponse>
   */
  static async getProductRoute(
    productName: string
  ): Promise<ProductRouteResponse> {
    try {
      const response = await fetch(
        `${this.baseUrl}/v1/public/product-route/${encodeURIComponent(productName)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Product route not found for: ${productName}`);
        }
        throw new Error(`API call failed: ${response.statusText}`);
      }

      // Handle 204 No Content - product exists but no route configured yet
      if (response.status === 204) {
        throw new Error(`No route configured for product: ${productName}`);
      }

      const data: ProductRouteResponse = await response.json();
      return data;
    } catch (error) {
      console.error("Failed to fetch product route data:", error);
      throw error;
    }
  }

  /**
   * Fetches app entrypoint data from the API with fallback logic
   * @param params - Parameters for determining app entrypoint
   * @returns Promise<ProductRouteResponse>
   */
  static async getAppEntrypoint(
    params: GetAppEntrypointParams
  ): Promise<ProductRouteResponse> {
    try {
      // If we have a product name, use the product-route endpoint
      if (params.productName) {
        return await this.getProductRoute(params.productName);
      }

      // Fallback to local logic if no product name is provided
      return this.getFallbackAppEntrypoint(params);
    } catch (error) {
      console.error("Failed to fetch app entrypoint data:", error);

      // Fallback to local logic if API fails
      return this.getFallbackAppEntrypoint(params);
    }
  }

  /**
   * Fallback logic when API is unavailable or no product name is provided
   * Uses the same logic as the current getAppEntrypoint function
   */
  static getFallbackAppEntrypoint(
    params: GetAppEntrypointParams
  ): ProductRouteResponse {
    // If user is disqualified, return mobile entrypoint
    if (params.isDisqualified) {
      return {
        product_name: params.productName || "unknown",
        app_entrypoint: AppEntrypoint.fh_plus_mobile,
        mso: undefined,
      };
    }

    // Check if running in React Native WebView and source is "app"
    if (window?.ReactNativeWebView && params.source === "app") {
      return {
        product_name: params.productName || "unknown",
        app_entrypoint: AppEntrypoint.fh_plus_mobile,
        mso: undefined,
      };
    }

    // Default fallback to Glide. /account is being removed, so a new user who
    // hits this path must not land on the Ionic web destination.
    return {
      product_name: params.productName || "unknown",
      app_entrypoint: AppEntrypoint.fh_plus_mobile,
      mso: undefined,
    };
  }

  /**
   * Legacy method for backward compatibility
   * @deprecated Use getAppEntrypoint instead
   */
  static async getMedicalProviderData(
    _surveySessionId?: string,
    _userId?: string
  ): Promise<{ medical_provider: string; app_entrypoint: AppEntrypoint }> {
    // This method is kept for backward compatibility
    // In practice, the new product-route endpoint should be used
    const fallback = this.getFallbackAppEntrypoint({
      surveySessionId: _surveySessionId,
      userAgent: navigator.userAgent,
    });
    return {
      medical_provider: fallback.mso || MSO.steadymd,
      app_entrypoint: fallback.app_entrypoint,
    };
  }
}
